import { BriefcaseIcon, HelpIcon, UserIcon } from '@coursera/cds-icons';

import { HELP_CENTER_LINK } from 'bundles/enterprise-admin-constants/help';
import type { IconComponent } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/types';
import { ACCOUNT_PATH, MANAGE_ADMINS_PATH } from 'bundles/enterprise-admin-navigation/utils/pathConstants';
import type { RequiredPermission } from 'bundles/enterprise-admin-roles/types';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

export type OrgSwitcherSection = 'settings' | 'help';

/**
 * Runtime context resolved once per render by `useAdminOrgSwitcherItems`.
 * Passed to every `shouldShow` predicate so items can make data-driven decisions
 * (e.g. "show this only if user has permission X or feature Y is enabled").
 *
 * Built from existing hooks (permissions, flags, features) so consumers stay
 * simple — no need to thread dependencies individually to every item.
 *
 * Properties:
 * - thirdPartyOrganization: Current organization data.
 * - hasPermission: Function to check if user has specific permission.
 * - isOrganizationDomainAdmin: Whether user is organization domain admin.
 * - enableNewLearnersPage: Feature flag for new learners page.
 * - enableWesBilling: Feature flag for WES billing (self-serve contracts).
 * - isDirectSale: Whether organization is a direct sale (vs. self-serve).
 */
export type OrgSwitcherRuntimeContext = {
  thirdPartyOrganization: AdminDashboardOrg;
  hasPermission: (perm: RequiredPermission) => boolean;
  isOrganizationDomainAdmin: boolean;
  enableNewLearnersPage: boolean;
  enableWesBilling: boolean;
  isDirectSale: boolean;
};

/**
 * OrgSwitcherItemConfig defines the shape of each item in the org-switcher popup.
 *
 * Properties:
 * - id: Unique string identifier for the item.
 * - getLabel: Function returning the item's display string.
 * - icon: IconComponent displayed alongside the label.
 * - href: Route template (':thirdPartySlug' substituted at runtime) or a fully-qualified URL.
 * - requiredPermissions: (Optional) Permission(s) needed to see/select this item.
 * - shouldShow: (Optional) Function that receives runtime context and returns true to show, false to hide.
 * - external: (Optional) If true, link opens in new tab (target=_blank, rel=noopener noreferrer).
 * - section: Section grouping, e.g., 'settings' or 'help'.
 */
export type OrgSwitcherItemConfig = {
  id: string;
  getLabel: () => string;
  icon: IconComponent;
  href: string;
  requiredPermissions?: RequiredPermission;
  shouldShow?: (runtimeContext: OrgSwitcherRuntimeContext) => boolean;
  external?: boolean;
  section: OrgSwitcherSection;
};

/**
 * The single source of truth for the org-switcher popup's static link items.
 *
 * To add: drop a new entry. To remove: delete the entry. Section ordering in
 * the rendered popup follows `settings → divider → help`.
 *
 * Exported as a constant to prevent array recreation on every hook invocation.
 * Labels are resolved at render time via the config predicates and resolvers.
 */
export const orgSwitcherConfigDefs: OrgSwitcherItemConfig[] = [
  /* eslint-disable no-restricted-syntax */
  // SETTINGS section — every item also requires `isOrganizationDomainAdmin` (today's behavior).
  {
    id: 'SELF_SERVE_CONTRACTS',
    getLabel: () => _t('Account & Billing'),
    icon: BriefcaseIcon,
    href: ACCOUNT_PATH,
    section: 'settings',
    shouldShow: (runtimeContext) => runtimeContext.isOrganizationDomainAdmin && runtimeContext.enableWesBilling,
  },
  {
    id: 'MANAGE_ADMINS',
    getLabel: () => _t('Manage Admins'),
    icon: UserIcon,
    href: MANAGE_ADMINS_PATH,
    section: 'settings',
    shouldShow: (runtimeContext) => runtimeContext.isOrganizationDomainAdmin && runtimeContext.enableNewLearnersPage,
  },

  // HELP section.
  {
    id: 'ADMIN_HELP',
    getLabel: () => _t('Admin Help Center'),
    icon: HelpIcon,
    href: HELP_CENTER_LINK,
    section: 'help',
    external: true,
  },
  {
    id: 'LEARNER_HELP',
    getLabel: () => _t('Learner Help Center'),
    icon: HelpIcon,
    href: 'https://learner.coursera.help/hc',
    section: 'help',
    external: true,
    shouldShow: (runtimeContext) => runtimeContext.isDirectSale,
  },
  /* eslint-enable no-restricted-syntax */
];
